/**
 * FinanceReportGrid — Dedicated grid for financial reports
 * Different from ERPAdvancedGrid — designed for accounting reports with:
 *  - Running balance column
 *  - Opening/Closing balance rows
 *  - Dr/Cr color coding
 *  - Export with balances
 *  - Footer summary (Opening, Current Total, Closing)
 */
import React, { useState, useMemo } from 'react';
import { Download, FileSpreadsheet, FileText, Printer, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { erpToast } from './ERPToast';
import '../../styles/erp-theme.css';

const FinanceReportGrid = ({
  title,
  columns,        // [{ field, header, align?, width?, mono?, isCurrency? }]
  data,
  footerRows,     // [{ label, values: { field: value } }] e.g. Opening Balance, Current Total, Closing Balance
  loading = false,
  emptyMessage = 'No records found',
  exportFileName,
  testId = 'finance-report',
  headerContent,  // JSX for filters above grid
  headerActions,  // JSX for extra action buttons in the header (e.g. Save button)
  summaryLine,    // e.g. "Total Count: 6  Total Amount: 6,17,517.00"
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [filterText, setFilterText] = useState('');

  const filtered = useMemo(() => {
    if (!filterText.trim()) return data;
    const s = filterText.toLowerCase();
    return data.filter(r => columns.some(c => String(r[c.field] || '').toLowerCase().includes(s)));
  }, [data, filterText, columns]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av || '').localeCompare(String(bv || '')) : String(bv || '').localeCompare(String(av || ''));
    });
  }, [filtered, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / pageSize) || 1;
  const pageData = sorted.slice((page - 1) * pageSize, page * pageSize);
  const fmtC = (v) => typeof v === 'number' ? v.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : (v || '');

  const handleExportCSV = () => {
    const headers = columns.map(c => c.header);
    const rows = sorted.map(r => columns.map(c => `"${String(r[c.field] || '').replace(/"/g, '""')}"`));
    if (footerRows) footerRows.forEach(fr => rows.push(columns.map(c => `"${fr.values[c.field] !== undefined ? fr.values[c.field] : (c.field === columns[0].field ? fr.label : '')}"`)));
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${exportFileName || title}.csv`; a.click();
    erpToast.success('Exported as CSV');
  };

  const handlePrint = () => {
    const hdr = columns.map(c => `<th style="border:1px solid #999;padding:6px 10px;background:#4A90D9;color:#fff;font-size:11px;text-align:${c.align || 'left'}">${c.header}</th>`).join('');
    const rows = sorted.map((r, i) => `<tr style="background:${i % 2 === 0 ? '#fff' : '#f0f5fa'}">` + columns.map(c => `<td style="border:1px solid #ddd;padding:4px 8px;font-size:11px;text-align:${c.align || 'left'};${c.mono ? 'font-family:monospace;' : ''}">${c.isCurrency ? fmtC(r[c.field]) : (r[c.field] || '')}</td>`).join('') + '</tr>').join('');
    let footerHtml = '';
    if (footerRows) footerRows.forEach(fr => { footerHtml += `<tr style="background:#e8eef4;font-weight:700">` + columns.map(c => `<td style="border:1px solid #ddd;padding:4px 8px;font-size:11px;text-align:${c.align || 'left'}">${fr.values[c.field] !== undefined ? (c.isCurrency ? fmtC(fr.values[c.field]) : fr.values[c.field]) : (c.field === columns[0].field ? fr.label : '')}</td>`).join('') + '</tr>'; });
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>@page{size:landscape;margin:10mm}body{font-family:Arial}</style></head><body><h3>${title}</h3>${summaryLine ? `<p>${summaryLine}</p>` : ''}<table style="border-collapse:collapse;width:100%"><thead><tr>${hdr}</tr></thead><tbody>${rows}${footerHtml}</tbody></table></body></html>`);
    win.document.close(); setTimeout(() => win.print(), 500);
  };

  return (
    <div className="erp-module" data-testid={testId}>
      {/* Header */}
      <div className="erp-screen-header">
        <span className="erp-screen-title">{title}</span>
        {summaryLine && <span style={{ fontSize: 12, color: 'var(--erp-primary)', fontWeight: 600, marginLeft: 16 }}>{summaryLine}</span>}
        <div className="erp-screen-actions">
          {headerActions && <>{headerActions}</>}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Search size={13} style={{ position: 'absolute', left: 8, color: 'var(--erp-text-muted)' }} />
            <input value={filterText} onChange={e => { setFilterText(e.target.value); setPage(1); }} placeholder="Search..." className="erp-input" style={{ paddingLeft: 28, width: 160, height: 26, fontSize: 11 }} data-testid={`${testId}-search`} />
          </div>
          <button className="erp-btn erp-btn-ghost" onClick={handleExportCSV} data-testid={`${testId}-export-csv`}><FileSpreadsheet size={13} /> CSV</button>
          <button className="erp-btn erp-btn-ghost" onClick={handlePrint} data-testid={`${testId}-print`}><Printer size={13} /> Print</button>
        </div>
      </div>

      {/* Filters */}
      {headerContent && <div style={{ padding: 'var(--erp-sp-3)', borderBottom: '1px solid var(--erp-border)' }}>{headerContent}</div>}

      {/* Grid */}
      <div className="erp-grid-wrapper" style={{ flex: 1, margin: 0, borderRadius: 0 }}>
        <div className="erp-grid-scroll">
          <table className="erp-grid" data-testid={`${testId}-table`}>
            <thead>
              <tr>{columns.map(c => (
                <th key={c.field} style={{ textAlign: c.align || 'left', width: c.width, cursor: 'pointer' }}
                  onClick={() => { if (sortField === c.field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(c.field); setSortDir('asc'); } setPage(1); }}>
                  {c.header} {sortField === c.field ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={columns.length} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading...</td></tr>
              : pageData.length === 0 ? <tr><td colSpan={columns.length} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>{emptyMessage}</td></tr>
              : pageData.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? 'var(--erp-surface)' : 'var(--erp-surface-alt)' }} data-testid={`${testId}-row-${i}`}>
                  {columns.map(c => (
                    <td key={c.field} style={{ textAlign: c.align || 'left', fontFamily: c.mono ? 'var(--erp-font-mono)' : undefined, fontSize: 12 }}>
                      {c.isCurrency ? fmtC(row[c.field]) : (row[c.field] || '')}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Footer summary rows */}
              {footerRows && !loading && pageData.length > 0 && footerRows.map((fr, fi) => (
                <tr key={`footer-${fi}`} style={{ background: 'var(--erp-primary-light)', fontWeight: 700, borderTop: fi === 0 ? '2px solid var(--erp-primary)' : undefined }}>
                  {columns.map((c, ci) => (
                    <td key={c.field} style={{ textAlign: c.align || 'left', fontFamily: c.mono ? 'var(--erp-font-mono)' : undefined, fontSize: 12, padding: '6px 8px' }}>
                      {fr.values[c.field] !== undefined ? (c.isCurrency ? fmtC(fr.values[c.field]) : fr.values[c.field]) : (ci === 0 ? fr.label : '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="erp-pagination" data-testid={`${testId}-pagination`}>
          <span>{sorted.length > 0 ? `${(page-1)*pageSize+1} to ${Math.min(page*pageSize, sorted.length)} from ${sorted.length} Records` : '0 Records'}</span>
          <div className="erp-pagination-controls">
            <span>Show Rows</span>
            <select value={pageSize} onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }} data-testid={`${testId}-page-size`}>
              {[25, 50, 100, 250, 500].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span>Page</span>
            <input type="number" value={page} onChange={e => setPage(Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1)))} style={{ width: 40 }} />
            <span>of {totalPages}</span>
            <button className="erp-pagination-btn" onClick={() => setPage(1)} disabled={page <= 1}><ChevronsLeft size={11} /></button>
            <button className="erp-pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}><ChevronLeft size={11} /></button>
            <button className="erp-pagination-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}><ChevronRight size={11} /></button>
            <button className="erp-pagination-btn" onClick={() => setPage(totalPages)} disabled={page >= totalPages}><ChevronsRight size={11} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceReportGrid;
